/**
 * Toolbox catalogue (#638).
 *
 * The customizable toolbox draws from the full tool catalogue (TOOL_KEYS in
 * ToolContext). A tool can appear in the toolbox only if it has display
 * metadata — a label, icon, and (optionally) a permission gate — which lives on
 * the matching `navigationConfig` entry. Tool keys without nav metadata
 * (internal-only sub-dialogs/tests) are excluded until metadata is added.
 *
 * `buildToolCatalogue` is pure: it joins a list of tool keys to flattened nav
 * metadata, preserving the order of the keys provided.
 */

export interface ToolRequires {
  feature: string;
  operation: string;
}

export interface CatalogueTool {
  key: string;
  label: string;
  icon: string;
  requires?: ToolRequires;
}

interface RawNavItem {
  type?: string;
  label?: string;
  tool?: string;
  icon?: string;
  requires?: ToolRequires;
  children?: RawNavItem[];
}

/**
 * Tools that are NOT in the main nav menu but should still be addable to a
 * toolbox (#683). The Support family collapsed to a single "Support" menu link;
 * these bug/feature/view-issues tools stay out of the menu yet available in the
 * toolbox catalogue. Menu-backed tools take precedence over these on key clash.
 */
export const CATALOGUE_ONLY_TOOLS: CatalogueTool[] = [
  { key: 'reportIssue', label: 'Report a bug', icon: 'fa-solid fa-bug', requires: { feature: 'support', operation: 'create' } },
  { key: 'requestFeature', label: 'Request feature', icon: 'fa-solid fa-lightbulb', requires: { feature: 'support', operation: 'create' } },
  { key: 'viewIssues', label: 'View issues', icon: 'fa-solid fa-list-check', requires: { feature: 'support', operation: 'read' } },
];

/** Flatten the nav tree into a tool-key → display-metadata index. */
function indexNavByTool(navItems: RawNavItem[]): Map<string, CatalogueTool> {
  const index = new Map<string, CatalogueTool>();
  const walk = (items: RawNavItem[]): void => {
    for (const item of items) {
      if (item.tool && item.label && item.icon && !index.has(item.tool)) {
        index.set(item.tool, {
          key: item.tool,
          label: item.label,
          icon: item.icon,
          ...(item.requires ? { requires: item.requires } : {}),
        });
      }
      if (item.children?.length) walk(item.children);
    }
  };
  walk(navItems);
  return index;
}

/**
 * Build the toolbox catalogue: one entry per tool key that has nav metadata,
 * in the order the keys are given.
 */
export function buildToolCatalogue(
  toolKeys: readonly string[],
  navItems: RawNavItem[],
): CatalogueTool[] {
  const index = indexNavByTool(navItems);
  // Merge in catalogue-only tools for keys the nav doesn't provide (nav wins on clash).
  for (const extra of CATALOGUE_ONLY_TOOLS) {
    if (!index.has(extra.key)) index.set(extra.key, extra);
  }
  const catalogue: CatalogueTool[] = [];
  for (const key of toolKeys) {
    const meta = index.get(key);
    if (meta) catalogue.push(meta);
  }
  return catalogue;
}

/**
 * Resolve the tools an active profile should actually show: the profile's tool keys,
 * in PROFILE order, keeping only those that (a) exist in the catalogue and (b) the user
 * is permitted to use. Tools whose catalogue entry has no permission gate are always kept.
 */
export function resolveActiveTools(
  profileToolKeys: string[],
  catalogue: CatalogueTool[],
  canDo: (feature: string, operation: string) => boolean,
): CatalogueTool[] {
  const byKey = new Map(catalogue.map((t) => [t.key, t]));
  const active: CatalogueTool[] = [];
  for (const key of profileToolKeys) {
    const tool = byKey.get(key);
    if (!tool) continue;
    if (tool.requires && !canDo(tool.requires.feature, tool.requires.operation)) continue;
    active.push(tool);
  }
  return active;
}
